import { Link2, PhoneIncoming, PhoneMissed, PhoneOutgoing } from 'lucide-react'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SkeletonRows } from '@/components/ui/skeleton'
import { formatDateTime, formatDuration } from '@/lib/formatters'
import {
  type TelephonyCallListItem,
  useAttachTelephonyCall,
  useTelephonyCalls,
} from '@/models/telephonyCalls'

interface Props {
  leadId: string
  open: boolean
  onClose: () => void
}

function directionIcon(call: TelephonyCallListItem) {
  if (call.answeredAt === null) return <PhoneMissed className="size-4 text-orange-600" />
  return call.direction === 'inbound' ? (
    <PhoneIncoming className="size-4 text-green-600" />
  ) : (
    <PhoneOutgoing className="size-4 text-green-600" />
  )
}

export function AttachCallDialog({ leadId, open, onClose }: Props) {
  const { data, isLoading } = useTelephonyCalls({ leadId }, { enabled: open })
  const attach = useAttachTelephonyCall()
  const calls = data?.results ?? []

  function handleAttach(call: TelephonyCallListItem) {
    attach.mutate(
      { callId: call.id, vendor: call.vendor, leadId },
      {
        onSuccess: () => toast.success('Call attached to lead'),
        onError: (err) => {
          const maybe = err as { response?: { data?: { error?: string } } }
          toast.error(maybe.response?.data?.error ?? 'Could not attach call')
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach a call</DialogTitle>
          <DialogDescription>Calls matching this lead's saved phone numbers.</DialogDescription>
        </DialogHeader>

        <div className="max-h-80 overflow-y-auto rounded-md border">
          {isLoading && <SkeletonRows className="p-3" rows={4} rowClassName="h-14" />}
          {!isLoading && calls.length === 0 && (
            <div className="text-muted-foreground p-4 text-sm">
              No calls found for this lead's saved numbers.
            </div>
          )}
          {!isLoading &&
            calls.map((call) => {
              const alreadyAttached = call.attachedLeads.some((l) => l.id === leadId)
              return (
                <div
                  key={`${call.vendor}-${call.id}`}
                  className="flex items-center justify-between gap-3 border-b px-3 py-2 last:border-b-0"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {directionIcon(call)}
                    <div className="min-w-0">
                      <div className="truncate font-mono text-xs">
                        {call.fromNumber} → {call.toNumber}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {formatDateTime(call.startedAt)}
                        {formatDuration(call.durationSeconds) && ` · ${formatDuration(call.durationSeconds)}`}
                      </div>
                    </div>
                  </div>
                  {alreadyAttached ? (
                    <Badge variant="outline">Attached</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      loading={
                        attach.isPending && attach.variables.callId === call.id ? 'Attaching…' : undefined
                      }
                      disabled={attach.isPending}
                      onClick={() => handleAttach(call)}
                    >
                      <Link2 className="size-4" />
                      Attach
                    </Button>
                  )}
                </div>
              )
            })}
        </div>

        <DialogFooter className="sm:justify-between">
          <Link
            to="/insure/internal/calls"
            className="text-muted-foreground text-sm underline-offset-2 hover:underline"
            onClick={onClose}
          >
            Not seeing the call? Browse all calls
          </Link>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
